import React from "react"
import { Icon as AlchemyIcon } from "@amzn/alchemy-components-react"

import { LocaleContext, Locale, isRtl, isHebrew } from "../i18n/LocaleContext"
import { AlchemyIconName } from "./AlchemyIconName"
import { IconName } from "./IconName"
import { IconNameMap } from "./IconNameMap"
import { IconColor } from "./IconColor"
import { IconSize, IconSizeMap } from "./IconSize"
import { LocalIconName } from "./LocalIconName"
import { LocalIconMap, isLocalIconName } from "./LocalIconMap"
import { shouldFlipLocalIcon } from "./LocalIconFlip"
import { DangerousProps } from "../helpers/DangerousProps"
import { TestProps } from "../helpers/TestProps"
import { renderTestID } from "../helpers/renderTestID"
import { LocalizedString, Translator } from "../i18n/Translator"
import { TranslatorContext } from "../i18n/TranslatorContext"

AlchemyIcon.displayName = "AlchemyIcon"

export interface IconProps extends DangerousProps, TestProps {
  name: IconName,
  size: IconSize,
  // No color means using the current text color
  color?: IconColor
  ariaLabel?: LocalizedString
}

/**
 * Most icons come from Alchemy. The ones Alchemy does not offer are declared in
 * `LocalIconMap` and rendered from an SVG asset in this package instead. Both
 * paths produce the same `icon` class contract, so callers never have to care
 * which one a given `IconName` uses.
 */
export class Icon extends React.PureComponent<IconProps> {
  public renderClassName(flipped: boolean) {
    const { color, size, dangerousClassName } = this.props
    return "icon"
                + ` icon--size-${size}`
                + (color ? ` icon--color-${color}` : "")
                + (flipped ? " icon--flip" : "")
                + (dangerousClassName ? ` ${dangerousClassName}` : "")
  }

  public renderWithAlchemy(name: AlchemyIconName, translator: Translator, locale: Locale) {
    const { size, ariaLabel } = this.props
    const content = (
      <AlchemyIcon
        className={this.renderClassName(false)}
        icon={name && IconNameMap[name]}
        label={ariaLabel ? translator.lookup(ariaLabel) : undefined}
        rtl={isRtl(locale)}
        hebrew={isHebrew(locale)}
        size={IconSizeMap[size]}
        // Fix the height to the desired size.
        // Due to an issue that will be soon fixed by Alchemy,
        // the SVG would actually go below the inline-box a bit,
        // causing vertical misalignment
        style={{
          height: `${IconSizeMap[size]}rem !important`,
        }}
        data-testid={renderTestID(this.props.testID)}
        aria-hidden={!ariaLabel ? true : undefined}
      />
    )
    return content
  }

  public renderLocalIcon(name: LocalIconName, translator: Translator, locale: Locale) {
    const { ariaLabel } = this.props
    const { component: LocalSvgIcon, flip } = LocalIconMap[name]
    // Unlike alchemy-icon, a bare <svg> gets no size, colour or mirroring of its
    // own, so all three come from the icon classes.
    return (
      <LocalSvgIcon
        className={this.renderClassName(shouldFlipLocalIcon(flip, locale))}
        style={this.props.dangerousStyle}
        data-testid={renderTestID(this.props.testID)}
        // Without an explicit role, some screen readers ignore aria-label on svg
        role={ariaLabel ? "img" : undefined}
        aria-label={ariaLabel ? translator.lookup(ariaLabel) : undefined}
        aria-hidden={!ariaLabel ? true : undefined}
      />
    )
  }

  public render() {
    const { name } = this.props
    return (
      <LocaleContext.Consumer>
        {(locale) => (
          <TranslatorContext.Consumer>
            {(translator) => {
              return isLocalIconName(name)
                ? this.renderLocalIcon(name, translator, locale)
                : this.renderWithAlchemy(name, translator, locale)
            }}
          </TranslatorContext.Consumer>
        )}
      </LocaleContext.Consumer>
    )
  }
}
